/**
 * Mappers for converting GraphQL maintenance types to legacy interface types
 *
 * These mappers enable gradual migration by allowing GraphQL data to be used
 * with existing UI components that expect the legacy interface types.
 */

import type {
  MaintenanceTicketGraphQL,
  AdminUserGraphQL,
} from './useMaintenanceTicketsRelay';
import type { MaintenanceTicketTagGraphQL } from './useMaintenanceTicketTagsRelay';
import type { MaintenanceLogGraphQL } from './useMaintenanceLogsRelay';
import type { ITicket, ITag, ITask } from '../../../../ts/interface/problem.interface';

/**
 * Format admin user name from GraphQL AdminUser
 */
function formatAdminName(user?: AdminUserGraphQL | null): string {
  if (!user) return '';
  const name = `${user.firstName} ${user.lastName}`.trim();
  return name || user.email;
}

/**
 * Map GraphQL MaintenanceTicket to legacy ITicket interface
 */
export function mapMaintenanceTicketToLegacy(ticket: MaintenanceTicketGraphQL): ITicket {
  const hardware = ticket.hardware;
  const task = ticket.maintenanceTask;
  const system = ticket.system;

  return {
    id: ticket.oldId ?? ticket.id,
    gid: ticket.id,
    old_id: ticket.oldId ?? '',
    due_on: ticket.dueOn,
    canceled_on: ticket.canceledOn ?? '',
    completed_on: ticket.completedOn ?? '',
    created_on: ticket.createdOn,
    assignee: ticket.assignee ?? '',
    assignee_name: formatAdminName(ticket.assigneeUser) || ticket.assignee || '',
    reporter: ticket.reporter ?? '',
    reporter_name: formatAdminName(ticket.reporterUser) || ticket.reporter || '',
    status: ticket.status ?? '',
    description: ticket.description,
    priority: ticket.priority,
    source_type: ticket.sourceType,
    communication_log: ticket.communicationLog
      ? JSON.stringify(ticket.communicationLog)
      : null,
    tags: ticket.tags ? [...ticket.tags] : [],
    // Hardware fields
    hardware_id: hardware?.oldId ?? ticket.hardwareOldId ?? '',
    hardware_gid: hardware?.id ?? '',
    hardware_number: hardware?.number ?? 0,
    hardware_type: hardware?.hardwareType?.name ?? '',
    hardware_type_name: hardware?.hardwareType?.name ?? '',
    primary_identifier: hardware?.hardwareProduct?.primaryIdentifier ?? '',
    mac: hardware?.mac ?? '',
    imei: hardware?.imei ?? '',
    vendor_serial: hardware?.vendorSerial ?? '',
    label: hardware?.label ?? '',
    // Current location of the ticket's hardware
    location_gid: hardware?.location?.id ?? '',
    location_name: hardware?.location?.name ?? '',
    // Task fields
    maintenance_task_id: ticket.maintenanceTaskId,
    task_name: task?.name ?? '',
    task_description: task?.description ?? '',
    task_type: task?.taskType ?? '',
    // Rental / error report
    error_report_id: ticket.errorReportOldId ?? '',
    error_report_gid: ticket.errorReport?.id ?? '',
    rental_id: ticket.rentalOldId ?? '',
    rental_gid: ticket.rental?.id ?? '',
    // System info
    system_id: ticket.systemOldId ?? '',
    system_info: system
      ? {
          id: system.id,
          old_id: system.oldId ?? '',
          name: system.name,
        }
      : undefined,
    images: ticket.images ? [...ticket.images] : [],
  } as ITicket;
}

/**
 * Map GraphQL MaintenanceTicketTag to legacy ITag interface
 */
export function mapMaintenanceTagToLegacy(tag: MaintenanceTicketTagGraphQL): ITag {
  return {
    id: tag.id,
    name: tag.name,
    created_on: tag.createdOn,
    archived_on: tag.archivedOn ?? '',
    system_info: tag.system
      ? {
          id: tag.system.id,
          name: tag.system.name,
          old_id: tag.system.oldId ? Number(tag.system.oldId) : 0,
        }
      : {
          id: '',
          name: '',
          old_id: 0,
        },
  };
}

/**
 * Map GraphQL MaintenanceLog to legacy ITask interface
 */
export function mapMaintenanceLogToLegacy(log: MaintenanceLogGraphQL): ITask {
  const hardware = log.hardware;
  const task = log.maintenanceTask;
  const location = log.location;
  const system = log.system;

  return {
    id: log.oldId ?? log.id,
    gid: log.id,
    created_on: log.createdOn,
    admin_user_id: log.adminUserId,
    admin_name: log.adminUser
      ? `${log.adminUser.firstName} ${log.adminUser.lastName}`.trim()
      : log.adminUserId,
    time_tracking: log.timeTracking,
    notes: log.notes ?? '',
    // Hardware fields
    hardware_id: hardware?.oldId ?? log.hardwareOldId ?? '',
    hardware_gid: hardware?.id ?? '',
    hardware_number: hardware?.number ?? 0,
    hardware_type_name: hardware?.hardwareType?.name ?? '',
    hardware_type: hardware?.hardwareType?.name ?? '',
    primary_identifier: hardware?.hardwareProduct?.primaryIdentifier ?? '',
    // Task fields
    maintenance_task_id: log.maintenanceTaskOldId,
    task_name: task?.name ?? '',
    task_description: task?.description ?? '',
    // Location fields
    station_gid: location?.id ?? '',
    station_name: location?.name ?? '',
    // Ticket fields
    maintenance_ticket_id: log.maintenanceTicket?.id ?? '',
    // System info
    system_info: system
      ? {
          id: system.id,
          old_id: system.oldId ?? '',
          name: system.name,
        }
      : undefined,
  } as ITask;
}

/**
 * Map arrays of GraphQL objects to legacy format
 */
export function mapMaintenanceTicketsToLegacy(
  tickets: MaintenanceTicketGraphQL[]
): ITicket[] {
  return tickets.map(mapMaintenanceTicketToLegacy);
}

export function mapMaintenanceTagsToLegacy(tags: MaintenanceTicketTagGraphQL[]): ITag[] {
  return tags.map(mapMaintenanceTagToLegacy);
}

export function mapMaintenanceLogsToLegacy(logs: MaintenanceLogGraphQL[]): ITask[] {
  return logs.map(mapMaintenanceLogToLegacy);
}
